import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Brain, Calendar, Tag, Link2, Archive, Pencil, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CATEGORY_COLORS, getCategoryLabel } from "./CategoryChips";
import { cn } from "@/lib/utils";

interface GraphNode {
  id: number;
  category: string;
  title: string;
  importance: number;
  createdAt: string | null;
  subcategory: string | null;
  sourceType: string | null;
}

interface ChunkDetail {
  chunk: GraphNode & { content: string };
  connections: Array<{ chunk: GraphNode; relationship: string; strength: number }>;
}

interface GraphDetailPanelProps {
  selectedNodeId: number | null;
  accountId?: number;
  brandId?: number;
  onSelectNode: (id: number) => void;
  onClose: () => void;
  className?: string;
}

export function GraphDetailPanel({ selectedNodeId, accountId, brandId, onSelectNode, onClose, className }: GraphDetailPanelProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: detail, isLoading } = useQuery<ChunkDetail>({
    queryKey: ["/api/heartbeat/chunks", selectedNodeId],
    queryFn: async () => {
      const res = await fetch(`/api/heartbeat/chunks/${selectedNodeId}`);
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
    enabled: !!selectedNodeId,
  });

  const [error, setError] = useState<string | null>(null);

  const handleArchive = async () => {
    if (!selectedNodeId) return;
    setError(null);
    const res = await fetch(`/api/heartbeat/chunks/${selectedNodeId}`, { method: "DELETE" });
    if (!res.ok) {
      setError("Failed to archive chunk");
      return;
    }
    if (brandId) {
      queryClient.invalidateQueries({ queryKey: ["/api/heartbeat/brands", brandId] });
    } else if (accountId) {
      queryClient.invalidateQueries({ queryKey: ["/api/heartbeat/accounts", accountId] });
    }
    onClose();
  };

  const handleSaveEdit = async () => {
    if (!selectedNodeId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/heartbeat/chunks/${selectedNodeId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: editTitle, content: editContent }),
      });
      if (!res.ok) {
        setError("Failed to save changes");
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["/api/heartbeat/chunks", selectedNodeId] });
      if (brandId) {
        queryClient.invalidateQueries({ queryKey: ["/api/heartbeat/brands", brandId] });
      } else if (accountId) {
        queryClient.invalidateQueries({ queryKey: ["/api/heartbeat/accounts", accountId] });
      }
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = () => {
    if (detail) {
      setEditTitle(detail.chunk.title);
      setEditContent(detail.chunk.content);
      setIsEditing(true);
    }
  };

  if (!selectedNodeId) {
    return (
      <div className={cn("bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center p-6 text-center", className)}>
        <Brain className="h-10 w-10 text-gray-300 mb-3" />
        <p className="text-sm font-medium text-gray-500">Select a node</p>
        <p className="text-xs text-gray-400 mt-1">Click a node in the graph to see details</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn("bg-white rounded-xl border border-gray-200 flex items-center justify-center p-6", className)}>
        <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!detail) return null;

  const { chunk, connections } = detail;
  const categoryColor = CATEGORY_COLORS[chunk.category] || "#6B7280";
  const dateStr = chunk.createdAt ? new Date(chunk.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Unknown";

  return (
    <div className={cn("bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden", className)} data-testid="detail-panel">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white"
            style={{ backgroundColor: categoryColor }}
          >
            {getCategoryLabel(chunk.category)}
          </span>
          <span className="text-[10px] text-gray-400 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {dateStr}
          </span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="button-close-detail">
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
        {isEditing ? (
          <div className="space-y-3">
            <Input
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              className="text-sm font-semibold"
              data-testid="input-edit-title"
            />
            <Textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className="text-sm min-h-[150px]"
              data-testid="input-edit-content"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveEdit} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600" data-testid="button-save-edit">
                {saving && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} data-testid="button-cancel-edit">Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="text-sm font-semibold text-gray-900" data-testid="text-chunk-title">{chunk.title}</h3>
            <div className="flex items-center gap-3 text-[11px] text-gray-400">
              {chunk.sourceType && (
                <span className="flex items-center gap-1"><Tag className="h-3 w-3" /> {chunk.sourceType}</span>
              )}
              <span>Importance: {chunk.importance}/10</span>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap" data-testid="text-chunk-content">{chunk.content}</p>
          </>
        )}

        {connections.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Link2 className="h-3 w-3" /> Connected Nodes ({connections.length})
            </h4>
            <div className="space-y-1.5">
              {connections.map(conn => (
                <button
                  key={conn.chunk.id}
                  onClick={() => onSelectNode(conn.chunk.id)}
                  className="w-full text-left p-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 group"
                  data-testid={`connection-node-${conn.chunk.id}`}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: CATEGORY_COLORS[conn.chunk.category] || "#6B7280" }}
                  />
                  <span className="text-xs text-gray-700 group-hover:text-gray-900 truncate flex-1">{conn.chunk.title}</span>
                  <span className="text-[10px] text-gray-400">{conn.relationship}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-100 flex-shrink-0">
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}
      {!isEditing && (
        <div className="px-4 py-2.5 border-t border-gray-100 flex gap-2 flex-shrink-0">
          <Button size="sm" variant="outline" onClick={startEdit} className="text-xs gap-1" data-testid="button-edit-chunk">
            <Pencil className="h-3 w-3" /> Edit
          </Button>
          <Button size="sm" variant="outline" onClick={handleArchive} className="text-xs gap-1 text-red-500 hover:text-red-600 hover:border-red-200" data-testid="button-archive-chunk">
            <Archive className="h-3 w-3" /> Archive
          </Button>
        </div>
      )}
    </div>
  );
}
